import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Grid,
  Stack,
  Card,
  CardContent,
  Checkbox,
  useMediaQuery,
  useTheme,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Avatar,
  Divider,
  IconButton
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import { getDatabase } from 'firebase/database';
import { initializeApp, getApp, getApps } from 'firebase/app';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import Footer from './components/Footer';
import PublicAiChatWidget from './components/PublicAiChatWidget';
import { BRAND, BRAND_GRADIENTS } from '../../config/brand';
import { nodeApi, mainApi } from '../../config/apiClient';
import { usePublicRegistrationFlow } from '../../contexts/PublicRegistrationFlowContext';

// Firebase yapılandırması
const firebaseConfig = {
  apiKey: "AIzaSyDDVbNobOjVnSF5-k4Q69xCvTvBp3cJsVM",
  authDomain: "gts-web-d2a3a.firebaseapp.com",
  databaseURL: "https://gts-web-d2a3a-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "gts-web-d2a3a",
  storageBucket: "gts-web-d2a3a.appspot.com",
  messagingSenderId: "826089296373",
  appId: "1:826089296373:web:8e97fc10c9a97a39f15b3d"
};

// Firebase uygulamasını başlat veya mevcut olanı kullan
let app;
try {
  app = getApp();
  console.log("Mevcut Firebase app kullanılıyor");
} catch (error) {
  app = initializeApp(firebaseConfig);
  console.log("Yeni Firebase app başlatıldı");
}

const database = getDatabase(app);

// Stil tanımlamaları
const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  background: BRAND_GRADIENTS.primary, // BRAND'den
}));



const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 16,
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  transition: 'transform 0.3s ease-in-out',
  backgroundColor: 'transparent',
  '&:hover': {
    transform: 'translateY(-10px)',
  },
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '250px',
  margin: '0 auto',
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  '& img': {
    width: 100,
    height: 100,
    objectFit: 'contain',
  },
}));

const ResponsiveGrid = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const SidePanelContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  width: '250px',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    marginBottom: theme.spacing(4),
  },
}));


const StatsSection = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  margin: theme.spacing(2, 0),
  border: '1px solid rgba(255, 255, 255, 0.2)'
}));

const ContentWrapper = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
});

const FormTextField = styled(TextField)(({ theme }) => ({
  margin: theme.spacing(0, 1),
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: 'white',
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '& .MuiInputBase-input': {
      fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
      fontWeight: 500,
      fontSize: '0.95rem',
      letterSpacing: '0.01em',
    }
  },
  '& .MuiFormLabel-root': {
    fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    fontWeight: 500,
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(0, 0, 0, 0.7)',
    opacity: 1,
    fontWeight: 500,
  },
  [theme.breakpoints.down('md')]: {
    margin: theme.spacing(1, 0),
    width: '100%',
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 3),
  borderRadius: 30,
  fontSize: '1rem',
  fontWeight: 'bold',
  textTransform: 'none',
  marginLeft: theme.spacing(2),
  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
  color: 'white',
  boxShadow: '0 4px 20px rgba(33, 150, 243, 0.4)',
  border: 0,
  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
  overflow: 'hidden',
  position: 'relative',
  '&:hover': {
    boxShadow: '0 6px 20px rgba(33, 150, 243, 0.6)',
    transform: 'translateY(-3px)',
    background: 'linear-gradient(45deg, #1976D2 30%, #00B0FF 90%)',
    '&::after': {
      opacity: 0.4,
    }
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow: '0 3px 10px rgba(33, 150, 243, 0.3)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '25%',
    width: '150%',
    height: '250%',
    background: 'linear-gradient(rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0))',
    transform: 'rotate(-45deg)',
    opacity: 0,
    transition: 'opacity 0.3s',
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: 0,
    width: '100%',
  },
}));

const RequiredMark = () => (
  <Typography component="span" sx={{ color: 'red', ml: 0.5 }}>*</Typography>
);

// Yeni stil tanımlamaları
const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(13, 71, 161, 0.9) 0%, rgba(25, 118, 210, 0.8) 100%)',
  padding: theme.spacing(8, 0),
  minHeight: '35vh',
  display: 'flex',
  alignItems: 'center',
  textAlign: 'center',
  color: 'white',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    opacity: 0.3
  }
}));

const StepCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  transition: 'transform 0.3s ease',
  height: '100%',
  borderRadius: 16,
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[8]
  }
}));

const FeatureBox = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  borderRadius: 16,
  padding: theme.spacing(3),
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  width: '100%',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
  }
}));

const IconCircle = styled(Avatar)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  width: 70,
  height: 70,
  marginBottom: theme.spacing(2),
  '& .MuiSvgIcon-root': {
    fontSize: 35,
    color: 'white'
  }
}));

// Tip tanımları
interface ChatBubbleProps {
  align?: 'left' | 'right';
  children: React.ReactNode;
}

const ChatBubble = styled(Box)<ChatBubbleProps>(({ theme, align = 'left' }) => ({
  backgroundColor: align === 'left' ? 'rgba(255, 255, 255, 0.1)' : '#1976D2',
  color: 'white',
  borderRadius: 20,
  padding: theme.spacing(2, 3),
  maxWidth: '80%',
  alignSelf: align === 'left' ? 'flex-start' : 'flex-end',
  marginBottom: theme.spacing(2),
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
}));

const GetStartedButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  borderRadius: 30,
  fontSize: '1.1rem',
  fontWeight: 'bold',
  textTransform: 'none',
  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
  color: 'white',
  boxShadow: '0 4px 20px rgba(33, 150, 243, 0.4)',
  marginTop: theme.spacing(4),
  minWidth: 200,
  '&:hover': {
    background: 'linear-gradient(45deg, #1976D2 30%, #00B0FF 90%)',
    boxShadow: '0 6px 20px rgba(33, 150, 243, 0.6)',
  }
}));

const Features: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openRoleSelection } = usePublicRegistrationFlow();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Modal durumu için state
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  // Resim büyütme modal durumu için state
  const [imageModalOpen, setImageModalOpen] = useState(false);

  // Firebase bağlantısını kontrol et
  useEffect(() => {
    console.log("Firebase bağlantısı kontrol ediliyor:", database);
    console.log("Firebase config:", firebaseConfig);
    
    // HTML ve body elementlerine overflow ekle
    document.documentElement.style.overflowY = 'auto';
    document.body.style.overflowY = 'auto';
    document.body.style.height = 'auto';
    
    return () => {
      // Component unmount olduğunda stilleri temizle
      document.documentElement.style.overflowY = '';
      document.body.style.overflowY = '';
      document.body.style.height = '';
    };
  }, []);

  // Form state
  const [formData, setFormData] = useState({
    nameSurname: '',
    email: '',
    phone: ''
  });

  // Bildirim state'i
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Modal kapatma işlevi
  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form gönderildi:', formData);
    
    setIsSubmitting(true);
    
    try {
      // İletişim talebini .NET API'ye kaydet (anonim, public)
      await mainApi.post('/api/ContactLeads/create', {
        nameSurname: formData.nameSurname,
        email: formData.email,
        phone: formData.phone,
        source: 'contact_form'
      });

      console.log("Veri başarıyla kaydedildi!");

      // Başarı modalını aç
      setSuccessModalOpen(true);

      // Belirli kullanıcılara bildirim gönder
      try {
        console.log("Belirli kullanıcılara bildirim gönderiliyor...");

        nodeApi.post('/api/send-specific-notification', {
          title: 'Yeni İletişim Talebi',
          body: `${formData.nameSurname} adlı kişi iletişime geçmek istiyor. Tel: ${formData.phone}, E-posta: ${formData.email}`,
          source: 'contact_form'
        })
        .then(response => {
          console.log("Bildirim gönderme sonucu:", response.data);
        })
        .catch(notificationError => {
          console.error("Bildirim gönderilirken hata:", notificationError);
          // Bildirim hatası olsa bile form gönderimi başarılı sayılır
        });
      } catch (notificationError) {
        console.error("Bildirim gönderimi sırasında hata:", notificationError);
        // Bildirim hatası olsa bile form gönderimi başarılı sayılır
      }

      // Form verilerini sıfırla
      setFormData({
        nameSurname: '',
        email: '',
        phone: ''
      });
    } catch (error) {
      // Hata olursa bildirim göster
      console.error('Form gönderme hatası:', error);
      setNotification({
        open: true,
        message: t('featuresPage.form.errorMessage') + (error as Error).message,
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Bildirim kapatma işlevi
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Resim modalını açma fonksiyonu
  const handleOpenImageModal = () => {
    setImageModalOpen(true);
  };

  // Resim modalını kapatma fonksiyonu
  const handleCloseImageModal = () => {
    setImageModalOpen(false);
  };


  const featureData = [
    {
      title: t('featuresPage.features.taskManagement.title'),
      description: t('featuresPage.features.taskManagement.description'),
      icon: <img src="/assets/gorev_takip_yonetimi.png" alt={t('featuresPage.features.taskManagement.title')} />,
      link: '/features/gorev-takibi'
    },
    {
      title: t('featuresPage.features.staffManagement.title'),
      description: t('featuresPage.features.staffManagement.description'),
      icon: <img src="/assets/personel_yonetimi.png" alt={t('featuresPage.features.staffManagement.title')} />,
      link: '/features/personel-yonetimi'
    },
    {
      title: t('featuresPage.features.shiftManagement.title'),
      description: t('featuresPage.features.shiftManagement.description'),
      icon: <img src="/assets/vardiya_takibi.png" alt={t('featuresPage.features.shiftManagement.title')} />,
      link: '/features/vardiya-takibi'
    },
    {
      title: t('featuresPage.features.reports.title'),
      description: t('featuresPage.features.reports.description'),
      icon: <img src="/assets/raporlar.png" alt={t('featuresPage.features.reports.title')} />,
      link: '/features/raporlar'
    },
    {
      title: t('featuresPage.features.notifications.title'),
      description: t('featuresPage.features.notifications.description'),
      icon: <img src="/assets/bildirimler.png" alt={t('featuresPage.features.notifications.title')} />,
      link: '/features/bildirimler'
    },
    {
      title: t('featuresPage.features.surveyManagement.title'),
      description: t('featuresPage.features.surveyManagement.description'),
      icon: <img src="/assets/anket_yonetimi.png" alt={t('featuresPage.features.surveyManagement.title')} />,
      link: '/features/anket-yonetimi'
    },
    {
      title: t('featuresPage.features.eventManagement.title'),
      description: t('featuresPage.features.eventManagement.description'),
      icon: <img src="/assets/etkinlik_yonetimi.png" alt={t('featuresPage.features.eventManagement.title')} />,
      link: '/features/etkinlik-yonetimi'
    },
    {
      title: t('featuresPage.features.branchManagement.title'),
      description: t('featuresPage.features.branchManagement.description'),
      icon: <img src="/assets/sube_yonetimi.png" alt={t('featuresPage.features.branchManagement.title')} />,
      link: '/features/sube-yonetimi'
    },
  ];

  const taskTrackingIncludedFeatures = [
    t('featuresPage.pricing.includedFeatures.taskAssignment'),
    t('featuresPage.pricing.includedFeatures.personnelManagement'),
    t('featuresPage.pricing.includedFeatures.shiftTracking'),
    t('featuresPage.pricing.includedFeatures.detailedReporting'),
    t('featuresPage.pricing.includedFeatures.instantNotifications'),
    t('featuresPage.pricing.includedFeatures.surveyManagement'),
    t('featuresPage.pricing.includedFeatures.eventManagement'),
    t('featuresPage.pricing.includedFeatures.branchManagement'),
    t('featuresPage.pricing.includedFeatures.qrEntryExit'),
    t('featuresPage.pricing.includedFeatures.qrTaskTracking'),
    t('featuresPage.pricing.includedFeatures.qrLocation'),
  ];

  return (
    <PageContainer>
      <Header />

      <ContentWrapper>
        {/* Hero Section */}
        <HeroSection>
          <Container maxWidth="lg">
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 800,
                mb: 2,
                background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              {t('featuresPage.hero.title')}
            </Typography>
            <Typography 
              variant="h4" 
              sx={{ 
                fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.4rem' },
                fontWeight: 400,
                mb: 4,
                opacity: 0.9,
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              {t('featuresPage.hero.subtitle')}
            </Typography>
            
            <Box sx={{ mb: 4 }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  mr: 2,
                  mb: { xs: 2, sm: 0 },
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  backgroundColor: 'white',
                  color: BRAND.colors.primary,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => openRoleSelection()}
              >
                {t('featuresPage.hero.ctaButton')}
              </Button>
              <Button
                variant="contained"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  backgroundColor: 'white',
                  color: BRAND.colors.primary,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => navigate('/contact')}
              >
                Hemen Başlayın
              </Button>
            </Box>

            {/* Quick Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2.5, flexWrap: 'wrap', mb: 4 }}>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 700,
                  px: 1.2,
                  py: 0.6,
                  '&:hover': { color: 'white' }
                }}
                onClick={() => {
                  const element = document.getElementById('features');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                Özellikler
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 700,
                  px: 1.2,
                  py: 0.6,
                  '&:hover': { color: 'white' }
                }}
                onClick={() => {
                  const element = document.getElementById('getStarted');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                Başlayın
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 700,
                  px: 1.2,
                  py: 0.6,
                  '&:hover': { color: 'white' }
                }}
                onClick={() => {
                  const element = document.getElementById('info');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                Bilgi
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '0.82rem', sm: '0.9rem', md: '0.95rem' },
                  fontWeight: 700,
                  px: 1.2,
                  py: 0.6,
                  '&:hover': { color: 'white' }
                }}
                onClick={() => {
                  const element = document.getElementById('mobile');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                Mobil
              </Button>
            </Box>

            {/* Sizi Arayalım Formu */}
            <StatsSection>
              <form onSubmit={handleSubmit} style={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '16px' : '8px'
              }}>
                <FormTextField
                  placeholder={t('featuresPage.form.namePlaceholder')}
                  variant="outlined"
                  name="nameSurname"
                  value={formData.nameSurname}
                  onChange={handleInputChange}
                  required
                  size="small"
                  InputProps={{
                    endAdornment: <RequiredMark />,
                    style: { 
                      fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif", 
                      fontWeight: 500 
                    }
                  }}
                  sx={{ flexGrow: 1, maxWidth: isMobile ? '100%' : '220px' }}
                />
                
                <FormTextField
                  placeholder={t('featuresPage.form.emailPlaceholder')}
                  variant="outlined"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  size="small"
                  InputProps={{
                    endAdornment: <RequiredMark />,
                    style: { 
                      fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif", 
                      fontWeight: 500 
                    }
                  }}
                  sx={{ flexGrow: 1, maxWidth: isMobile ? '100%' : '220px' }}
                />
                
                <FormTextField
                  placeholder={t('featuresPage.form.phonePlaceholder')}
                  variant="outlined"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  size="small"
                  InputProps={{
                    endAdornment: <RequiredMark />,
                    style: { 
                      fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif", 
                      fontWeight: 500 
                    }
                  }}
                  sx={{ flexGrow: 1, maxWidth: isMobile ? '100%' : '220px' }}
                />
                
                <SubmitButton 
                  type="submit" 
                  variant="contained"
                  endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('featuresPage.form.sendingButton') : t('featuresPage.form.sendButton')}
                </SubmitButton>
              </form>
            </StatsSection>
          </Container>
        </HeroSection>

        {/* Pricing Section */}
        <Box sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              textAlign="center"
              sx={{
                mb: 6,
                fontWeight: 700,
                color: BRAND.colors.primary,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              {t('featuresPage.pricing.title')}
            </Typography>

            <Grid container spacing={3} justifyContent="center" sx={{ mb: 1 }}>
              {[{ key: 'yearly', price: 50, popular: true }, { key: 'monthly', price: 70, popular: false }].map((plan) => (
                <Grid item xs={12} md={6} key={plan.key}>
                  <Paper
                    elevation={plan.popular ? 6 : 1}
                    sx={{
                      borderRadius: 3,
                      overflow: 'hidden',
                      border: `2px solid ${plan.popular ? BRAND.colors.primary : '#e8eaf0'}`,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      backgroundColor: 'white',
                    }}
                  >
                    <Box
                      sx={{
                        height: 44,
                        backgroundColor: plan.popular ? BRAND.colors.primary : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {plan.popular && (
                        <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.95rem', letterSpacing: 1.4 }}>
                          {t('featuresPage.pricing.banner')}
                        </Typography>
                      )}
                    </Box>

                    <Box
                      sx={{
                        backgroundColor: plan.popular ? `${BRAND.colors.primary}12` : '#fafbfc',
                        px: 3,
                        pt: 3,
                        pb: 2.5,
                        borderBottom: `1px solid ${plan.popular ? `${BRAND.colors.primary}25` : '#eee'}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          letterSpacing: 2.2,
                          color: BRAND.colors.primary,
                          textTransform: 'uppercase',
                          mb: 1.4,
                        }}
                      >
                        {plan.key === 'yearly' ? t('featuresPage.pricing.yearlyPackage') : t('featuresPage.pricing.monthlyPackage')}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1 }}>
                        <Typography sx={{ fontSize: '3.2rem', fontWeight: 900, color: '#111', lineHeight: 1, letterSpacing: -1.5 }}>
                          {plan.price}
                        </Typography>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111', mb: 0.1 }}>TL</Typography>
                        <Typography sx={{ fontSize: '1rem', color: '#666', ml: 0.6 }}>{t('featuresPage.pricing.perUserPerMonth')}</Typography>
                      </Box>

                      <Typography sx={{ fontSize: '0.86rem', color: plan.popular ? BRAND.colors.primary : '#52b788', fontWeight: 700 }}>
                        {plan.popular ? t('featuresPage.pricing.yearlyAdvantage') : t('featuresPage.pricing.monthlyFlex')}
                      </Typography>
                    </Box>

                    <Box sx={{ px: 3, pt: 2.2, pb: 1, flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, color: '#111', fontSize: '0.92rem', mb: 0.3 }}>
                        {t('featuresPage.pricing.includedTitle')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: '#9aa0a6', mb: 1.2 }}>
                        {t('featuresPage.pricing.includedSubtitle')}
                      </Typography>

                      <Box
                        sx={{
                          maxHeight: 260,
                          overflowY: 'auto',
                          pr: 0.5,
                          '&::-webkit-scrollbar': { width: 8, borderRadius: 4 },
                          '&::-webkit-scrollbar-track': { background: '#f0f0f0', borderRadius: 4 },
                          '&::-webkit-scrollbar-thumb': { background: BRAND.colors.primary, borderRadius: 4 },
                        }}
                      >
                        {taskTrackingIncludedFeatures.map((feature) => (
                          <Box
                            key={`${plan.key}-${feature}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              border: `1.5px solid ${BRAND.colors.primary}30`,
                              borderRadius: 2,
                              p: 1.1,
                              mb: 1,
                              backgroundColor: `${BRAND.colors.primary}08`,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Checkbox
                                checked
                                disabled
                                size="small"
                                sx={{
                                  p: 0,
                                  color: '#43a047 !important',
                                  '&.Mui-checked': { color: '#43a047 !important' },
                                }}
                              />
                              <Typography sx={{ fontWeight: 600, fontSize: '0.84rem', color: '#1b1f24' }}>
                                {feature}
                              </Typography>
                            </Box>
                            <Typography
                              sx={{
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                color: BRAND.colors.primary,
                                border: `1px solid ${BRAND.colors.primary}55`,
                                borderRadius: 6,
                                px: 0.9,
                                py: 0.25,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {t('featuresPage.pricing.includedChip')}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    <Box sx={{ px: 3, pb: 3, pt: 1.5 }}>
                      <Button
                        variant={plan.popular ? 'contained' : 'outlined'}
                        fullWidth
                        size="large"
                        onClick={() => openRoleSelection()}
                        sx={{
                          py: 1.5,
                          fontWeight: 700,
                          fontSize: '1rem',
                          borderRadius: 2,
                          ...(plan.popular
                            ? {
                                backgroundColor: BRAND.colors.primary,
                                boxShadow: `0 4px 16px ${BRAND.colors.primary}40`,
                                '&:hover': {
                                  opacity: 0.92,
                                  backgroundColor: BRAND.colors.primary,
                                  boxShadow: `0 6px 20px ${BRAND.colors.primary}55`
                                }
                              }
                            : {
                                color: BRAND.colors.primary,
                                borderColor: BRAND.colors.primary,
                                '&:hover': { borderColor: BRAND.colors.primary, backgroundColor: `${BRAND.colors.primary}08` }
                              })
                        }}
                      >
                        {t('featuresPage.pricing.button')}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Features Section */}
        <Box id="features" sx={{ py: 8, backgroundColor: 'white' }}>
          <Container maxWidth="lg">
            <Typography 
              variant="h2" 
              textAlign="center" 
              sx={{ 
                mb: 6,
                fontWeight: 700,
                color: BRAND.colors.primary,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              {t('featuresPage.features.title')}
            </Typography>
            <Grid container spacing={3}>
              {featureData.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <FeatureCard elevation={0} onClick={() => navigate(feature.link)} sx={{ cursor: 'pointer' }}>
                    <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <IconWrapper>
                        {feature.icon}
                      </IconWrapper>
                      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', mb: 1.5, color: '#333', fontSize: '1rem' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ textAlign: 'center', color: '#666', fontSize: '0.85rem' }}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Get Started Section */}
        <Box id="getStarted" sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
          <Container maxWidth="lg">
            <Typography 
              variant="h2" 
              textAlign="center" 
              sx={{ 
                mb: 6,
                fontWeight: 700,
                color: BRAND.colors.primary,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              {t('featuresPage.getStarted.sectionTitle')}
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <StepCard>
                  <Typography variant="h6" fontWeight="bold" color={BRAND.colors.primary} gutterBottom>
                    {t('featuresPage.getStarted.step1.title')}
                  </Typography>
                  <Typography color="#666">
                    {t('featuresPage.getStarted.step1.description')}
                  </Typography>
                </StepCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <StepCard>
                  <Typography variant="h6" fontWeight="bold" color={BRAND.colors.primary} gutterBottom>
                    {t('featuresPage.getStarted.step2.title')}
                  </Typography>
                  <Typography color="#666">
                    {t('featuresPage.getStarted.step2.description')}
                  </Typography>
                </StepCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <StepCard>
                  <Typography variant="h6" fontWeight="bold" color={BRAND.colors.primary} gutterBottom>
                    {t('featuresPage.getStarted.step3.title')}
                  </Typography>
                  <Typography color="#666">
                    {t('featuresPage.getStarted.step3.description')}
                  </Typography>
                </StepCard>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Info Section */}
        <Box id="info" sx={{ py: 8, backgroundColor: 'white' }}>
          <Container maxWidth="lg">
            <Typography 
              variant="h2" 
              textAlign="center" 
              sx={{ 
                mb: 6,
                fontWeight: 700,
                color: BRAND.colors.primary,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              {t('featuresPage.info.title')}
            </Typography>
            <Typography color="#666" align="center" sx={{ maxWidth: '700px', mx: 'auto', mt: 2, mb: 4, fontSize: '1.1rem' }}>
              {t('featuresPage.info.p1')}
            </Typography>
            <Typography color="#666" align="center" sx={{ maxWidth: '700px', mx: 'auto', mb: 4, fontSize: '1.1rem' }}>
              {t('featuresPage.info.p2')}
            </Typography>
            <Typography color="#666" align="center" sx={{ maxWidth: '700px', mx: 'auto', fontSize: '1.1rem' }}>
              {t('featuresPage.info.p3')}
            </Typography>
          </Container>
        </Box>

        {/* Mobile App Section */}
        <Box id="mobile" sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
          <Container maxWidth="lg">
            <Typography 
              variant="h2" 
              textAlign="center" 
              sx={{ 
                mb: 6,
                fontWeight: 700,
                color: BRAND.colors.primary,
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              {t('featuresPage.mobile.title')}
            </Typography>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography color="#666" sx={{ mb: 2, fontSize: '1.1rem' }}>
                  {t('featuresPage.mobile.p1')}
                </Typography>
                <Typography color="#666" sx={{ mb: 4, fontSize: '1.1rem' }}>
                  {t('featuresPage.mobile.p2')}
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<AndroidIcon />}
                    onClick={() => window.open('https://play.google.com/store/apps/details?id=com.mt_teknoloji.gts', '_blank')}
                    sx={{ 
                      backgroundColor: BRAND.colors.primary,
                      '&:hover': { 
                        backgroundColor: '#1976D2'
                      },
                      borderRadius: 12,
                      padding: theme.spacing(1.2),
                      fontWeight: 'bold',
                      textTransform: 'none',
                      height: '43px'
                    }}
                  >
                    {t('buttons.googlePlay')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AppleIcon />}
                    onClick={() => window.open('https://apps.apple.com/tr/app/gts-g%C3%B6rev-takip-sistemi/id6743622025?l=tr&platform=iphone', '_blank')}
                    sx={{ 
                      backgroundColor: BRAND.colors.primary,
                      '&:hover': { 
                        backgroundColor: '#1976D2'
                      },
                      borderRadius: 12,
                      padding: theme.spacing(1.2),
                      fontWeight: 'bold',
                      textTransform: 'none',
                      height: '43px'
                    }}
                  >
                    {t('buttons.appStore')}
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box 
                    component="img" 
                    src="/assets/mobil.png" 
                    alt={t('featuresPage.mobile.imageAlt')} 
                    sx={{ 
                      maxWidth: '100%', 
                      height: 'auto', 
                      maxHeight: '400px',
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                    onClick={handleOpenImageModal}
                  />
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
        <Footer />
      </ContentWrapper>

      {/* Bildirim bileşeni */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Başarı Modalı */}
      <Dialog
        open={successModalOpen}
        onClose={handleCloseSuccessModal}
        aria-labelledby="success-dialog-title"
        aria-describedby="success-dialog-description"
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            padding: 2,
            maxWidth: 500
          }
        }}
      >
        <DialogTitle id="success-dialog-title" sx={{ textAlign: 'center', pt: 3 }}>
          <CheckCircleOutlineIcon sx={{ 
            fontSize: 70, 
            color: 'success.main',
            display: 'block',
            margin: '0 auto',
            mb: 2
          }} />
          <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
            {t('featuresPage.successModal.title')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText 
            id="success-dialog-description"
            sx={{ 
              textAlign: 'center',
              fontSize: '1.1rem',
              color: 'text.primary',
              mb: 2
            }}
          >
            {t('featuresPage.successModal.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            onClick={handleCloseSuccessModal} 
            color="primary"
            variant="contained"
            sx={{
              borderRadius: 30,
              px: 4,
              py: 1,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              boxShadow: '0 4px 20px rgba(33, 150, 243, 0.4)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1976D2 30%, #00B0FF 90%)',
                boxShadow: '0 6px 20px rgba(33, 150, 243, 0.6)',
              }
            }}
          >
            {t('featuresPage.successModal.confirmButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resim Büyütme Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={handleCloseImageModal}
        maxWidth="lg"
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible'
          }
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton 
            onClick={handleCloseImageModal}
            sx={{ 
              position: 'absolute', 
              top: -20, 
              right: -20,
              bgcolor: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.8)'
              }
            }}
          >
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>×</Typography>
          </IconButton>
          <img 
            src="/assets/mobil.png" 
            alt={t('featuresPage.mobile.imageAlt')} 
            style={{
              maxWidth: '100%',
              maxHeight: '80vh',
              objectFit: 'contain'
            }}
          />
        </Box>
      </Dialog>

      {/* WhatsApp Floating Button */}
      <PublicAiChatWidget />
    </PageContainer>
  );
};

export default Features; 